import { clearCurrentSession, setCurrentSession } from '@jwplayer/companion/session/cache';
import { auth, type GetOutput } from '@jwplayer/companion-gateway';
import { getWuiOverlaysApi } from '@jwplayer/wui-react';

import { sitesNameCache } from '/component/site-select-input';
import { populateAccountLimits } from '/module/account';
import { redirect } from '/module/routing/module';
import { setSentryUser } from '/module/sentry';
import { getInitialActiveSiteId, setCurrentSiteId } from '/module/sites';
import { hydrateQueryCache as hydrateContentProtectionQueryCache } from '/module/sites/content-protection';
import { hydrateQueryCache as hydrateDrmEnabledQueryCache } from '/module/sites/drm-enabled';

import { SessionStore } from './index';

async function updateSiteInformation(siteId: string) {
  SessionStore.update((s) => {
    s.currentSiteId = siteId;
  });

  if (siteId) {
    await Promise.all([
      // Populate the site-specific information
      hydrateContentProtectionQueryCache(siteId),
      hydrateDrmEnabledQueryCache(siteId),
    ]);
  }
}

export async function createSession(data?: Parameters<typeof auth.login>[0]) {
  // This function should not contain (or minimize) the amount of blocking API calls. We don't
  // want a user to be unable to login because one (or more) API calls is failing.

  const session = data ? await auth.login(data) : await auth.authenticate();

  const initialActiveSiteId = getInitialActiveSiteId(session);

  // Share session with companion v1 and set the active site id there
  shareSessionWithCompanionV1(session);
  setCurrentSiteId(initialActiveSiteId, session.default_site);

  // Asynchronously retrieve account limits information. It doesn't matter if this fails.
  populateAccountLimits();

  // Retrieve additional information
  await Promise.all([
    // Populate query cache for the current site
    updateSiteInformation(initialActiveSiteId),

    // Populate the sites name cache for the site-select input
    sitesNameCache.populate(),
  ]);

  // Set sentry user
  setSentryUser(session);

  function handleSessionChange(session: GetOutput<'dashboard-scope', 'create'> | GetOutput<'dashboard-scope', 'read'>) {
    // Share any changes to the session to old companion, so that it may use
    // this session to make api calls
    shareSessionWithCompanionV1(session);

    // Ensure our local store is always up to date
    SessionStore.update((s) => {
      s.session = modifySessionForDashboard(session);
    });
  }

  // When auth automatically refreshes
  auth.subscribe('authenticated', (_token, session) => {
    handleSessionChange(session);
  });

  // When auth cache is modified (for example, user changes settings)
  auth.subscribe('cacheModified', (session) => {
    handleSessionChange(session);
  });

  // When auth refresh has failed
  auth.subscribe('refreshFailed', () => {
    cleanupSession();
  });

  // When auth token is no longer valid (computer/tab was inactive for too long)
  auth.subscribe('expired', () => {
    cleanupSession();
  });

  SessionStore.update((s) => {
    s.session = modifySessionForDashboard(session);
  });

  return session;
}

/**
 * Modify the session object for use in the dashboard.
 *
 * Will sort sites in alphabetical order.
 */
function modifySessionForDashboard<T extends GetOutput<'dashboard-scope', 'read'>>(
  session: T | undefined,
): T | undefined {
  if (typeof session === 'undefined') {
    return undefined;
  }

  return {
    ...session,
    sites: [...session.sites].sort((a, b) => {
      return Intl.Collator().compare(a.name.toLowerCase(), b.name.toLowerCase());
    }),
  };
}

export function destroySession() {
  auth.logout();

  cleanupSession(false);
}

function cleanupSession(showToast = true) {
  redirect({
    to: {
      pathname: 'login',
    },
  });

  // Clean up session in companion v1
  shareSessionWithCompanionV1(undefined);

  setSentryUser(undefined);

  sitesNameCache.reset();

  SessionStore.update((s) => {
    s.currentSiteId = undefined;
    s.session = undefined;
    s.accountLimits = {
      isStreamingStopped: false,
      isStorageStopped: false,
      isAdsStopped: false,
    };
  });

  if (showToast) {
    getWuiOverlaysApi().Toast.createAndOpen({
      msg: 'Your session has expired. Please log in again.',
      type: 'error',
      timeout: undefined,
    });
  }
}

export function shareSessionWithCompanionV1(session: GetOutput<'dashboard-scope', 'read'> | undefined) {
  if (session) {
    setCurrentSession(
      // we don't need to be type-strict for the old companion
      session as never,
    );
  } else {
    clearCurrentSession();
  }
}
